import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import maplibregl, { LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl'
import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { NavigationStart, Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from '../layers-config/layers'
import { MapClusterIcons, MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { DataMatchedSignsService } from '../services/data-matched-signs.service'
import { withSignTypeFilter } from '../utils/property-filter.utils'
import { get } from 'lodash'

@Injectable()
export class LayerMatchedSignsService {
  private layer = this.layerPrefix
  public layerLinesId = `${this.layer}-lines`
  public layerSymbolsId = `${this.layer}-symbols`
  public layerSubsSymbolsId = `${this.layer}-subs-symbols`

  public layerSymbolsClusterId = `${this.layer}-symbols-cluster`
  public sourceLineId = `${this.layer}-line-source`
  public sourceSymbolsId = `${this.layer}-symbol-source`
  public sourceSymbolsCountId = `${this.layer}-symbol-count-source`

  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  private timespan: number | undefined
  public selectedFeatureId: string = ''
  public selectedSegmentId: string = ''
  public layerInitialized: boolean = false
  public onDestroy: boolean = false
  public clusterMarkers: maplibregl.Marker[] = []
  public whileReload = false

  private matchedSignsBaseFilter: maplibregl.ExpressionSpecification = [
    'all',
    ['!', ['has', 'point_count']],
  ]
  private matchedSignsSubTypesBaseFilter: maplibregl.ExpressionSpecification = [
    'all',
    ['!', ['has', 'point_count']],
    ['>', ['length', ['get', 'supplementalSign']], 0],
  ]

  private matchedSignsLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSymbolsId,
    source: this.sourceSymbolsId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.matchedSignsBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'bottom',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-allow-overlap': true,

      'icon-image': [
        'case',
        // Speed limit signs with numeric values
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'case',
            ['==', ['get', 'speedLimit'], null],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_NULL_DIGITAL'],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit']],
        ],
        // Speed limit end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit']],
        ],

        // Speed limit zone signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit']],
        ],

        // Speed limit zone end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit']],
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.YIELD],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.YIELD, '_DIGITAL'],
          MapSignsIcons.YIELD,
        ],

        // Simple icon mappings (no concatenation needed)
        ['==', ['get', 'mainType'], MapSignsIcons.ALL_RESTRICTIONS_END],
        MapSignsIcons.ALL_RESTRICTIONS_END,

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_START],
        MapSignsIcons.CITY_START,

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_END],
        MapSignsIcons.CITY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY],
        MapSignsIcons.HIGHWAY,

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY_END],
        MapSignsIcons.HIGHWAY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY],
        MapSignsIcons.MOTORWAY,

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY_END],
        MapSignsIcons.MOTORWAY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING],
        MapSignsIcons.TRAFFIC_CALMING,

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING_END],
        MapSignsIcons.TRAFFIC_CALMING_END,

        // Default fallback for unknown types
        MapSignsIcons.UNKNOWN,
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.15, 0.12],
        7,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.25, 0.2],
        13,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.6, 0.5],
        18,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 1, 0.8],
      ],
    },
    paint: {
      'icon-opacity': 1,
    },
  }

  private matchedSignsSubTypesWarningEventsLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSubsSymbolsId,
    source: this.sourceSymbolsId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.matchedSignsSubTypesBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'center',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-allow-overlap': true,
      'icon-image': [
        'case',
        // Single supplemental sign - use different icon
        ['==', ['length', ['get', 'supplementalSign']], 1],
        ['concat', ['get', 'type', ['at', 0, ['get', 'supplementalSign']]]],
        // Multiple supplemental signs - use existing unknown icon
        [
          'concat',
          MapSupsIcons.SUP_UNKNOWN,
          '_',
          ['to-string', ['length', ['get', 'supplementalSign']]],
        ],
      ],
      // NEW: combine selected case with zoom scaling
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.15, 0.12],
        7,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.25, 0.2],
        13,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.6, 0.5],
        18,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 1, 0.8],
      ],
      'icon-offset': [0, 25],
    },
    paint: {
      'icon-opacity': 1,
    },
  }

  private matchedSignsClusterLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSymbolsClusterId,
    source: this.sourceSymbolsId,
    maxzoom: 12,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: ['has', 'point_count'],
    layout: {
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.15, 0.12],
        7,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.25, 0.2],
        13,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.6, 0.5],
        18,
        ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 1, 0.8],
      ],
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-image': MapClusterIcons.TSC,
      'text-allow-overlap': true,
      'text-field': ['get', 'point_count_abbreviated'],
      'text-anchor': 'center',
      'symbol-z-order': 'source',
    },
  }

  private matchedSignsSegmentWarningLayerSpecification: LineLayerSpecification = {
    id: this.layerLinesId,
    type: 'line',
    minzoom: 8,
    source: this.sourceLineId, // reference the data source
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    filter: ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
    paint: {
      'line-color': 'white',
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        14,
        [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          6,
          ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
          6,
          2,
        ],
        18,
        [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          14,
          ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
          14,
          10,
        ],
        22,
        [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          18,
          ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
          18,
          14,
        ],
      ],
    },
  }

  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layerPrefix: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataMatchedSignsService,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  private async initializeLayer() {
    this.mapService.map.addSource(this.sourceLineId, {
      ...emptyGeoJsonSource,
    })

    this.mapEventsService.registerForMapEvents({ layerId: this.layerSymbolsId }, true)
    this.mapService.map?.addSource(this.sourceSymbolsId, {
      ...emptyGeoJsonSource,
      cluster: true,
      clusterMaxZoom: 11, // Max zoom to cluster points on
      clusterRadius: 30,
      promoteId: 'id',
    })
    this.mapService.map.addLayer(this.matchedSignsSegmentWarningLayerSpecification)
    this.mapService.map.addLayer(this.matchedSignsSubTypesWarningEventsLayerSpecification)
    this.mapService.map.addLayer(this.matchedSignsClusterLayerSpecification)
    this.mapService.map?.addLayer(this.matchedSignsLayerSpecification)

    this.layerInitialized = true

    // Immediately update the layer properties with the correct selectedFeatureId
    if (this.selectedFeatureId && this.selectedFeatureId !== '') {
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
    this.evaluateState()
  }

  ngOnDestroy() {
    this.onDestroy = true

    this.mapService.map.removeLayer(this.layerLinesId)

    this.mapEventsService.detachMapEvents({ layerId: this.layerSymbolsId })
    this.mapService.map.removeLayer(this.layerSymbolsClusterId)
    this.mapService.map.removeLayer(this.layerSubsSymbolsId)
    this.mapService.map.removeLayer(this.layerSymbolsId)
    this.mapService.map.removeSource(this.sourceSymbolsId)
    this.mapService.map.removeSource(this.sourceLineId)

    // Cancel all requests and clean up properly
    this.cancelRequests()
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  // Update your cancelRequests method to properly cancel HTTP requests
  private cancelRequests() {
    // Cancel requests in the data service
    this.dataService.cancelCurrentRequests()
  }

  // Update your reload method to handle cancellation gracefully
  public async reload() {
    // Cancel any previous requests first
    this.cancelRequests()

    if (this.mapService.isMapReady && this.mapService.map.getZoom() >= this.minZoom) {
      await this.dataService.loadMatchedSignsData(
        this.appState.isHistoryEnabled(),
        this.mapService.getExpandedBounds(0.5),
        this.appState.selectTimestamp(),
      )
      this.updateLayer()
    }
  }

  public async updateLayer() {
    if (this.mapService.isMapReady && this.whileReload == false) {
      const matchedSigns = this.dataService.getMatchedSigns()
      this.mapService.setGeoJsonData(this.sourceSymbolsId, matchedSigns[0])
      this.mapService.setGeoJsonData(this.sourceLineId, matchedSigns[1])
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private async evaluateState() {
    const layers = this.appState.selectLayers()
    if (layers.some((layer) => layer === Layers.MatchedSigns.name)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }
    // if layer is not visible stop all events
    if (this.isLayerVisible === false) {
      return
    }

    this.applySignTypeFilter()

    if (!this.timestamp) this.timestamp = this.appState.selectTimestamp()

    if (!this.dataService.isDataInitialized) {
      await this.reload()
    }

    if (
      this.appState.hasLayerVisibilityChanged(this.layer) &&
      !this.appState.getState().historyEnabled
    ) {
      await this.reload()
    }

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
      await this.reload()
    }

    if (this.appState.hasTimestampChanged()) {
      this.timestamp = this.appState.selectTimestamp()
      await this.reload()
    }

    if (
      this.appState.hasSelectedFeatureIdChanged() &&
      this.appState.getState().selectedFeatureId !== ''
    ) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.updateLayer()
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private applySignTypeFilter() {
    const selectedSignTypes = this.appState.selectSignTypes()
    const propertyFilters = this.appState.selectPropertyFilters()

    if (this.mapService.map.getLayer(this.layerSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSymbolsId,
        withSignTypeFilter(this.matchedSignsBaseFilter, selectedSignTypes, propertyFilters),
      )
    }

    if (this.mapService.map.getLayer(this.layerSubsSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSubsSymbolsId,
        withSignTypeFilter(this.matchedSignsSubTypesBaseFilter, selectedSignTypes, propertyFilters),
      )
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      this.mapService.setLayerVisibility(this.layerSymbolsId, isVisible)
      this.mapService.setLayerVisibility(this.layerLinesId, isVisible)
      this.mapService.setLayerVisibility(this.layerSymbolsClusterId, isVisible)
      this.mapService.setLayerVisibility(this.layerSubsSymbolsId, isVisible)
      if (!isVisible) {
        // Cancel any ongoing requests when layer becomes invisible
        this.cancelRequests()
      }
    }
  }

  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return
    this.mapService.map.setLayoutProperty(this.layerSymbolsId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.15, 0.12],
      7,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.25, 0.2],
      13,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.6, 0.5],
      18,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 1.0, 0.8],
    ])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.15, 0.12],
      7,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.25, 0.2],
      13,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 0.6, 0.5],
      18,
      ['case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''], 1.0, 0.8],
    ])

    this.mapService.map.setLayoutProperty(this.layerSymbolsId, 'symbol-sort-key', [
      'case',
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      1000000000000, // High value to bring selected feature to front
      0,
    ])

    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'symbol-sort-key', [
      'case',
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      1000000000000, // High value to bring selected feature to front
      0,
    ])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-offset', [0, 25])

    this.mapService.map.setFilter(this.layerLinesId, [
      '==',
      ['to-string', ['get', 'id']],
      this.selectedFeatureId || '',
    ])
  }

  private getSelectedFeatureExpression(
    selectedFeatureId: string,
  ): maplibregl.ExpressionSpecification {
    return ['any', ['==', ['to-string', ['get', 'id']], selectedFeatureId || '']]
  }
}
